import React, { useState } from 'react';
import { FlaskConical, Router, Play, Loader2, Radio, Database, ChevronRight, ListRestart } from 'lucide-react';
import toast from 'react-hot-toast';
import { testingAPI } from '../services/api';
import useDarkReady from '../theme/useDarkReady';

// Isolated per-step trigger panel for "Fetch All Broadband OLT Data" (OLT Management page).
// Built 2026-09-02 so an admin can fire each step on its own and watch for the OLT to
// reboot, rather than only ever being able to run the whole bulk action at once.
//
// Read this before treating a click here as evidence: the 2026-08-26 investigation already
// tested "our request pattern crashes the OLT" at the strictest possible level (a global
// request-serialization mutex — literally one HTTP request to the OLT in flight at a time,
// ever) and the OLT still rebooted while the request-trace log showed traffic completely
// calm and idle at the exact reboot instant. That's hard evidence against the request-volume
// theory. These buttons are still useful for controlled isolation/timing correlation, but a
// reboot during Step 1 or Step 4 here isn't new proof of an app-side cause — cross-check
// against the OLT's own event log before drawing conclusions. Full writeup: memory
// project_olt_reboot_fetchall.md.
const STEPS = [
  {
    key: 'step1',
    title: '1. Fetch ONU list',
    text: 'Pulls the OLT’s full ONU list once, forcing a fresh scan (bypasses the 2-minute cache) — the same 3-pass, 16-port scan the real feature runs first.',
    touchesOlt: true,
    call: testingAPI.oltStep1OnuList,
    render: (d) => `${d.count} ONUs in ${d.tookMs}ms`,
  },
  {
    key: 'step2',
    title: '2. Match by username (dry run)',
    text: 'Matches every broadband customer (customerType: broadband or both) by PPPoE username against each ONU’s label/description. Dry run — no DB writes.',
    touchesOlt: false,
    call: testingAPI.oltStep2MatchUsernames,
    render: (d) => `${d.matched}/${d.total} matched, ${d.unresolved} unresolved — ${d.tookMs}ms`,
  },
  {
    key: 'step3',
    title: '3. Write matches to customer records',
    text: 'Same matching pass as Step 2, but actually writes the router’s serial number, model, and PON port onto each matched customer record — no review step.',
    touchesOlt: false,
    call: testingAPI.oltStep3WriteDb,
    render: (d) => `${d.updated}/${d.total} updated (${d.matched} matched) — ${d.tookMs}ms`,
  },
  {
    key: 'step4',
    title: '4. MAC-address fallback pass',
    text: 'For customers a username match couldn’t place: falls back to MAC-address matching (NAS/Netzur lookup + the OLT’s own MAC-info page), one at a time, 1.5s apart.',
    touchesOlt: true,
    call: testingAPI.oltStep4MacFallback,
    render: (d) => `${d.macMatched}/${d.unresolvedTried} matched via MAC — ${d.tookMs}ms`,
  },
  {
    key: 'step5',
    title: '5. Pause + resume alarm polling',
    text: 'Pauses the alarm-polling service, then resumes it immediately — resuming runs one full alarm tick (its own OLT scan) before rearming the 60s interval.',
    touchesOlt: true,
    call: testingAPI.oltStep5PauseResumeAlarm,
    render: (d) => `paused ${d.pausedMs}ms, resumed (incl. 1 tick) in ${d.tookMs}ms`,
  },
  {
    key: 'step5a',
    title: '5a. Pause alarm polling only',
    text: 'Pauses the alarm-polling service and leaves it paused — nothing else will touch the OLT in the background until you run 5b. Use this before stepping through 1b/1c/1d or 4b/4c/4d for a fully isolated single request.',
    touchesOlt: false,
    call: testingAPI.oltStep5aPauseAlarm,
    render: (d) => `paused in ${d.tookMs}ms`,
  },
  {
    key: 'step5b',
    title: '5b. Resume alarm polling only',
    text: 'Resumes the alarm-polling service — runs one full tick (its own OLT scan) immediately, then rearms the 60s interval.',
    touchesOlt: true,
    call: testingAPI.oltStep5bResumeAlarm,
    render: (d) => `resumed (incl. 1 tick) in ${d.tookMs}ms`,
  },
  {
    key: 'step6',
    title: '6. Coverage summary',
    text: 'Read-only snapshot: total broadband customers vs. how many already have router serial/model/PON data saved. No OLT or alarm-service interaction.',
    touchesOlt: false,
    call: testingAPI.oltStep6Summary,
    render: (d) => `${d.withSerial}/${d.total} have OLT data saved — ${d.tookMs}ms`,
  },
];

function StepCard({ step, result, running, onRun }) {
  return (
    <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '.95rem' }}>{step.title}</div>
          <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{step.text}</div>
        </div>
        <span style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: '.68rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '.02em', padding: '3px 8px', borderRadius: 999,
          color: step.touchesOlt ? 'var(--c-b45309)' : 'var(--c-374151)', background: step.touchesOlt ? 'var(--cb-fef3c7)' : 'var(--cb-f3f4f6)',
        }}>
          {step.touchesOlt ? <Radio size={11} /> : <Database size={11} />}
          {step.touchesOlt ? 'Hits OLT' : 'DB only'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRun} disabled={running}>
          {running ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <Play size={13} style={{ marginRight: 5 }} />}
          {running ? 'Running…' : 'Run'}
        </button>
        {result && !result.error && (
          <span style={{ fontSize: '.8rem', color: 'var(--c-059669)', fontWeight: 600 }}>{step.render(result.data)}</span>
        )}
        {result?.error && (
          <span style={{ fontSize: '.8rem', color: 'var(--danger)', fontWeight: 600 }}>{result.error}</span>
        )}
        {result && (
          <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {new Date(result.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
      </div>
    </div>
  );
}

// Step 1 broken into its three individual per-port requests, one port at a time — so each
// single OLT hit inside the 48-request scan (16 ports × 3 passes) can be fired and watched
// on its own instead of only ever running as one unattended burst. Load the port list once,
// then for each port run the status pass, optical pass, and auth pass as separate clicks.
function PortStepper({ onHistory }) {
  const [ports, setPorts] = useState(null);
  const [index, setIndex] = useState(0);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [state, setState] = useState({}); // { status, optical, auth }
  const [busy, setBusy] = useState(null); // 'status' | 'optical' | 'auth' | null

  const port = ports && ports[index];

  const record = (title, ok, summary) => {
    onHistory({ key: `step1-${title}`, title, at: Date.now(), ok, summary });
  };

  const loadPorts = async () => {
    setLoadingPorts(true);
    try {
      const { data } = await testingAPI.oltStep1aPorts();
      setPorts(data.data.ports);
      setIndex(0);
      setState({});
      record('1a. Load ports', true, `${data.data.ports.length} PON ports loaded`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('1a. Load ports', false, message);
      toast.error(`Load failed: ${message}`);
    } finally {
      setLoadingPorts(false);
    }
  };

  const runPass = async (key, label, call) => {
    if (!port) return;
    setBusy(key);
    try {
      const { data } = await call(port);
      setState(prev => ({ ...prev, [key]: data.data.count }));
      record(label, true, `Port ${port}: ${data.data.count} ONUs in ${data.data.tookMs}ms`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record(label, false, message);
      toast.error(`${label} failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const next = () => {
    setIndex(i => i + 1);
    setState({});
  };

  return (
    <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '.95rem' }}>1a–1d. Fetch ONU list, one port at a time</div>
        <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
          Same 3-pass scan as Step 1 above, split into its individual pieces — load the PON port list once,
          then for each port run the status pass, optical pass, and auth pass as separate clicks, watching
          the OLT between each, instead of all 48 requests firing unattended.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={loadPorts} disabled={loadingPorts}>
          {loadingPorts ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <ListRestart size={13} style={{ marginRight: 5 }} />}
          {ports ? 'Reload port list' : 'Load port list'}
        </button>
        {ports && (
          <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
            {ports.length} ports — port {ports.length ? index + 1 : 0}/{ports.length}
          </span>
        )}
      </div>

      {port != null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4, borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: '.86rem' }}>Port {port}</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={next} disabled={index >= ports.length - 1}>
              Next port <ChevronRight size={13} style={{ marginLeft: 3 }} />
            </button>
          </div>

          <SubStepRow
            label="1b. Status pass (onustatusinfo.html)"
            touchesOlt={true}
            busy={busy === 'status'}
            disabled={busy !== null}
            onRun={() => runPass('status', '1b. Status pass', testingAPI.oltStep1bStatus)}
            result={state.status !== undefined ? `${state.status} ONUs` : null}
          />
          <SubStepRow
            label="1c. Optical pass (pononuopticalinfo.html)"
            touchesOlt={true}
            busy={busy === 'optical'}
            disabled={busy !== null}
            onRun={() => runPass('optical', '1c. Optical pass', testingAPI.oltStep1cOptical)}
            result={state.optical !== undefined ? `${state.optical} ONUs` : null}
          />
          <SubStepRow
            label="1d. Auth pass (onuauthinfo.html)"
            touchesOlt={true}
            busy={busy === 'auth'}
            disabled={busy !== null}
            onRun={() => runPass('auth', '1d. Auth pass', testingAPI.oltStep1dAuth)}
            result={state.auth !== undefined ? `${state.auth} ONUs` : null}
          />
        </div>
      )}
    </div>
  );
}

// Step 4 broken into four separately-clickable pieces, one customer at a time — so each
// individual OLT hit inside the MAC-fallback loop (resolve MAC → OLT MAC lookup → OLT
// write) can be fired and watched on its own, instead of only the whole per-customer loop
// running unattended. Load the list once, then step through customers with Next, running
// however many of the three sub-steps you want to watch for each before moving on.
function MacFallbackStepper({ onHistory }) {
  const [list, setList] = useState(null);
  const [index, setIndex] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [state, setState] = useState({}); // { mac, macAt, onuId, onuAt, write, writeAt, single }
  const [busy, setBusy] = useState(null); // 'mac' | 'lookup' | 'write' | 'single' | null

  const current = list && list[index];

  const record = (title, ok, summary) => {
    onHistory({ key: `step4-${title}`, title, at: Date.now(), ok, summary });
  };

  const loadList = async () => {
    setLoadingList(true);
    try {
      const { data } = await testingAPI.oltStep4aUnresolvedList();
      setList(data.data.customers);
      setIndex(0);
      setState({});
      record('4a. List unresolved', true, `${data.data.customers.length} unresolved customers loaded`);
      toast.success(`${data.data.customers.length} unresolved customers loaded`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('4a. List unresolved', false, message);
      toast.error(`Load failed: ${message}`);
    } finally {
      setLoadingList(false);
    }
  };

  // The whole pipeline (resolve MAC → sleep → OLT MAC lookup → OLT write → save) for the
  // current customer, in one click — same result as running 4b, 4c and 4d back to back,
  // but one button instead of three. Click, watch the OLT, click "Next customer", repeat.
  const runSingle = async () => {
    if (!current) return;
    setBusy('single');
    try {
      const { data } = await testingAPI.oltStep4Single(current.id);
      setState({ mac: data.data.mac, onuId: data.data.onuId, single: data.data });
      const summary = !data.data.mac
        ? 'no MAC resolvable (NAS/Netzur) — never touched the OLT'
        : !data.data.onuId
          ? `MAC ${data.data.mac} not in OLT MAC table`
          : `${data.data.mac} → ${data.data.onuId}: ${data.data.oltSynced ? 'synced + saved' : 'write failed'}`;
      record('4. Single-click run', data.data.mac ? !!data.data.updated || !data.data.onuId : true, `${current.name}: ${summary}`);
      if (data.data.updated) toast.success('Matched, written to OLT, and saved');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('4. Single-click run', false, message);
      toast.error(`Run failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const resolveMac = async () => {
    if (!current) return;
    setBusy('mac');
    try {
      const { data } = await testingAPI.oltStep4bResolveMac(current.id);
      setState({ mac: data.data.mac, macAt: Date.now() });
      record('4b. Resolve MAC', true, `${current.name}: ${data.data.mac || 'not found'}`);
      if (!data.data.mac) toast('No MAC resolvable for this customer (NAS/Netzur) — nothing to look up on the OLT', { icon: '⚠️' });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('4b. Resolve MAC', false, message);
      toast.error(`Resolve MAC failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const lookupMac = async () => {
    if (!state.mac) return;
    setBusy('lookup');
    try {
      const { data } = await testingAPI.oltStep4cMacLookup(state.mac);
      setState(prev => ({ ...prev, onuId: data.data.onuId, onuAt: Date.now() }));
      record('4c. OLT MAC lookup', true, `${state.mac}: ${data.data.onuId || 'not found in OLT MAC table'}`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('4c. OLT MAC lookup', false, message);
      toast.error(`OLT MAC lookup failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const writeLabel = async () => {
    if (!current || !state.onuId) return;
    setBusy('write');
    try {
      const { data } = await testingAPI.oltStep4dWriteLabel(current.id, state.onuId);
      setState(prev => ({ ...prev, write: data.data, writeAt: Date.now() }));
      record('4d. Write to OLT', data.data.oltSynced, `${current.name} → ${state.onuId}: ${data.data.oltSynced ? 'synced + saved' : 'failed'}`);
      if (data.data.oltSynced) toast.success('Written to OLT and saved');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      record('4d. Write to OLT', false, message);
      toast.error(`Write failed: ${message}`);
    } finally {
      setBusy(null);
    }
  };

  const next = () => {
    setIndex(i => i + 1);
    setState({});
  };

  return (
    <div className="stat-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '.95rem' }}>4a–4d. MAC fallback, one request at a time</div>
        <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
          Same pipeline as Step 4 above, split into its individual pieces — load the unresolved-by-username
          list once, then for each customer run Resolve MAC (no OLT), OLT MAC lookup (one macinfo.html hit),
          and Write to OLT (one onudetail.html write) as separate clicks, watching the OLT between each.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={loadList} disabled={loadingList}>
          {loadingList ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <ListRestart size={13} style={{ marginRight: 5 }} />}
          {list ? 'Reload unresolved list' : 'Load unresolved list'}
        </button>
        {list && (
          <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
            {list.length} unresolved — customer {list.length ? index + 1 : 0}/{list.length}
          </span>
        )}
      </div>

      {list && list.length === 0 && (
        <div style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>No unresolved customers right now — nothing to step through.</div>
      )}

      {current && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4, borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: '.86rem' }}>{current.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({current.username})</span></div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={next} disabled={index >= list.length - 1}>
              Next customer <ChevronRight size={13} style={{ marginLeft: 3 }} />
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={runSingle} disabled={busy !== null}>
              {busy === 'single' ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <Play size={13} style={{ marginRight: 5 }} />}
              {busy === 'single' ? 'Running…' : 'Run this customer (1 click)'}
            </button>
            {state.single && (
              <span style={{ fontSize: '.8rem', fontWeight: 600, color: state.single.updated ? 'var(--c-059669)' : 'var(--text-muted)' }}>
                {!state.single.mac ? 'no MAC resolvable' : !state.single.onuId ? 'not in OLT MAC table' : state.single.oltSynced ? 'synced + saved' : 'write failed'}
              </span>
            )}
          </div>

          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', margin: '-4px 0 2px' }}>— or step through this same customer one request at a time —</div>

          <SubStepRow
            label="4b. Resolve MAC (NAS/Netzur — no OLT)"
            touchesOlt={false}
            busy={busy === 'mac'}
            disabled={busy !== null}
            onRun={resolveMac}
            result={state.mac !== undefined ? (state.mac || 'not found') : null}
          />
          <SubStepRow
            label="4c. OLT MAC lookup (macinfo.html)"
            touchesOlt={true}
            busy={busy === 'lookup'}
            disabled={busy !== null || !state.mac}
            onRun={lookupMac}
            result={state.onuId !== undefined ? (state.onuId || 'not found in OLT MAC table') : null}
          />
          <SubStepRow
            label="4d. Write to OLT (onudetail.html — a write)"
            touchesOlt={true}
            busy={busy === 'write'}
            disabled={busy !== null || !state.onuId}
            onRun={writeLabel}
            result={state.write ? (state.write.oltSynced ? 'synced + saved to customer' : 'failed') : null}
            ok={state.write?.oltSynced}
          />
        </div>
      )}
    </div>
  );
}

function SubStepRow({ label, touchesOlt, busy, disabled, onRun, result, ok }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: '.66rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '.02em', padding: '3px 7px', borderRadius: 999, width: 78, justifyContent: 'center',
        color: touchesOlt ? 'var(--c-b45309)' : 'var(--c-374151)', background: touchesOlt ? 'var(--cb-fef3c7)' : 'var(--cb-f3f4f6)',
      }}>
        {touchesOlt ? <Radio size={10} /> : <Database size={10} />}
        {touchesOlt ? 'Hits OLT' : 'DB only'}
      </span>
      <span style={{ fontSize: '.82rem', minWidth: 260 }}>{label}</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onRun} disabled={disabled}>
        {busy ? <Loader2 size={13} className="spin" style={{ marginRight: 5 }} /> : <Play size={13} style={{ marginRight: 5 }} />}
        {busy ? 'Running…' : 'Run'}
      </button>
      {result !== null && result !== undefined && (
        <span style={{ fontSize: '.8rem', fontWeight: 600, color: ok === false ? 'var(--danger)' : 'var(--c-059669)' }}>{result}</span>
      )}
    </div>
  );
}

export default function TestingPage() {
  useDarkReady();
  const [results, setResults] = useState({});
  const [running, setRunning] = useState({});
  const [history, setHistory] = useState([]);

  const run = async (step) => {
    setRunning(prev => ({ ...prev, [step.key]: true }));
    const at = Date.now();
    try {
      const { data } = await step.call();
      setResults(prev => ({ ...prev, [step.key]: { data: data.data, at } }));
      setHistory(prev => [{ key: step.key, title: step.title, at, ok: true, summary: step.render(data.data) }, ...prev].slice(0, 30));
      toast.success(`${step.title} done`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed';
      setResults(prev => ({ ...prev, [step.key]: { error: message, at } }));
      setHistory(prev => [{ key: step.key, title: step.title, at, ok: false, summary: message }, ...prev].slice(0, 30));
      toast.error(`${step.title} failed: ${message}`);
    } finally {
      setRunning(prev => ({ ...prev, [step.key]: false }));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FlaskConical size={22} /> Testing
          </h1>
          <p className="page-subtitle">
            Isolated triggers for each step of "Fetch All Broadband OLT Data" — run one at a time to correlate timing against OLT reboots.
          </p>
        </div>
      </div>

      <div className="stat-card" style={{ padding: 14, marginBottom: 16, fontSize: '.82rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Router size={16} />
        Fetch All Broadband OLT Data — OLT Management page. Only Steps 1, 4 and 5 generate real traffic to the OLT; Steps 2, 3 and 6 are DB/computation only. See the page comment for why a reboot here isn't automatic proof of an app-side cause.
      </div>

      <div style={{ display: 'grid', gap: 14, marginBottom: 14 }}>
        {STEPS.map(step => (
          <StepCard
            key={step.key}
            step={step}
            result={results[step.key]}
            running={!!running[step.key]}
            onRun={() => run(step)}
          />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
        <PortStepper onHistory={(h) => setHistory(prev => [h, ...prev].slice(0, 30))} />
        <MacFallbackStepper onHistory={(h) => setHistory(prev => [h, ...prev].slice(0, 30))} />
      </div>

      {history.length > 0 && (
        <div className="stat-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: '.88rem', marginBottom: 10 }}>Run history (this session)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: '.78rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0, width: 80 }}>
                  {new Date(h.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <span style={{ fontWeight: 600, flexShrink: 0 }}>{h.title}</span>
                <span style={{ color: h.ok ? 'var(--c-059669)' : 'var(--danger)' }}>{h.summary}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
